/**
 * GeoServiceLandingPage — Renders AI-generated city × service SEO pages.
 * Route: /locations/:stateSlug/:citySlug/:serviceId
 * Content comes from /api/seo/geo-page/{state}/{city}/{service}
 */
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEO from '../SEO';
import { Button } from '../ui/button';
import { MapPin, Phone, ArrowRight, Loader2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_NAMES = {
  'web-design': 'Web Design',
  'seo': 'Local SEO',
  'ai-visibility': 'AI Visibility',
  'social-media': 'Social Media Marketing',
  'google-business': 'Google Business Profile',
  'content-marketing': 'Content Marketing',
  'ppc': 'PPC & Google Ads',
};

export default function GeoServiceLandingPage() {
  const { stateSlug, citySlug, serviceId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchPage() {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/seo/geo-page/${stateSlug}/${citySlug}/${serviceId}`);
        if (!res.ok) {
          if (!cancelled) setNotFound(true);
        } else {
          const data = await res.json();
          if (!cancelled) setPage(data);
        }
      } catch {
        if (!cancelled) setNotFound(true);
      }
      if (!cancelled) setLoading(false);
    }
    fetchPage();
    return () => { cancelled = true; };
  }, [stateSlug, citySlug, serviceId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="geo-page-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <noscript>
          <div style={{ maxWidth: 900, margin: '40px auto', padding: 20, fontFamily: 'system-ui,sans-serif' }}>
            <h1>{SERVICE_NAMES[serviceId] || serviceId} Services in {citySlug?.replace(/-/g, ' ')}</h1>
            <p>Professional {SERVICE_NAMES[serviceId]} services for businesses in {citySlug?.replace(/-/g, ' ')}.</p>
            <p><a href="/book-consultation">Get a free consultation</a> — call (817) 264-7476.</p>
          </div>
        </noscript>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md px-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="geo-not-found">Page Not Found</h1>
          <p className="text-slate-600 mb-6">
            We don't have a page for {citySlug?.replace(/-/g, ' ')} × {SERVICE_NAMES[serviceId] || serviceId} yet — but we serve businesses nationwide.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/locations')} data-testid="browse-locations-btn">Browse Locations</Button>
            <Button variant="outline" onClick={() => navigate('/book-consultation')} data-testid="book-consult-btn">
              Book a Consultation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const c = page.content || {};
  // Canonical points to the clean indexable URL (this same SPA path). Crawlers
  // are served the prerendered HTML at this path via the Express proxy, so the
  // canonical is self-referencing — no longer the robots-blocked /api/ URL.
  const canonical = `https://web-techservices.com/locations/${stateSlug}/${citySlug}/${serviceId}`;
  const serviceName = page.service_name || SERVICE_NAMES[serviceId] || serviceId;

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Web-TechServices — ${serviceName} in ${page.city_name}`,
    "description": c.meta_description,
    "url": canonical,
    "telephone": "+1-817-264-7476",
    "areaServed": { "@type": "City", "name": page.city_name },
    "address": { "@type": "PostalAddress", "addressLocality": page.city_name, "addressRegion": page.state_abbr, "addressCountry": "US" },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": canonical + "#service",
    "serviceType": serviceName,
    "name": `${serviceName} in ${page.city_name}, ${page.state_abbr}`,
    "description": c.meta_description || `Professional ${serviceName} services for businesses in ${page.city_name}, ${page.state_abbr}.`,
    "url": canonical,
    "areaServed": { "@type": "City", "name": page.city_name, "containedInPlace": { "@type": "State", "name": page.state_abbr } },
    "provider": {
      "@type": "LocalBusiness",
      "name": "Web-TechServices",
      "telephone": "+1-817-264-7476",
      "url": "https://web-techservices.com/",
    },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": canonical + "#breadcrumb",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://web-techservices.com/" },
      { "@type": "ListItem", "position": 2, "name": "Locations", "item": "https://web-techservices.com/locations" },
      { "@type": "ListItem", "position": 3, "name": page.city_name, "item": `https://web-techservices.com/locations/${stateSlug}/${citySlug}` },
      { "@type": "ListItem", "position": 4, "name": serviceName, "item": canonical },
    ],
  };

  const faqSchema = Array.isArray(c.faq) && c.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": c.faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-white" data-testid="geo-service-page">
      <SEO
        title={c.meta_title || `${serviceName} in ${page.city_name}, ${page.state_abbr} | Web-TechServices`}
        description={c.meta_description || `Professional ${serviceName} services for businesses in ${page.city_name}, ${page.state_abbr}.`}
        keywords={(c.longtail_keywords || []).join(', ')}
        canonical={canonical}
        schema={[serviceSchema, schema, faqSchema, breadcrumb].filter(Boolean)}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-900 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-blue-300 text-sm mb-4">
            <MapPin className="h-4 w-4" />
            <Link to="/locations" className="hover:underline">Locations</Link>
            <span>/</span>
            <Link to={`/locations/${stateSlug}`} className="hover:underline capitalize">
              {stateSlug?.replace(/-/g, ' ')}
            </Link>
            <span>/</span>
            <Link to={`/locations/${stateSlug}/${citySlug}`} className="hover:underline">
              {page.city_name}
            </Link>
            <span>/</span>
            <span className="text-white">{serviceName}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="geo-page-h1">
            {c.h1 || `${serviceName} in ${page.city_name}, ${page.state_abbr}`}
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl" data-testid="geo-page-intro"
             dangerouslySetInnerHTML={{ __html: c.intro || '' }} />
          <div className="flex flex-wrap gap-3 mt-8">
            <Button size="lg" onClick={() => navigate('/book-consultation')}
                    className="bg-white text-blue-900 hover:bg-blue-50" data-testid="geo-book-btn">
              Book Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="tel:+18172647476"
               className="inline-flex items-center gap-2 border-2 border-white/30 px-5 py-2.5 rounded-md hover:bg-white/10 transition">
              <Phone className="h-4 w-4" /> (817) 264-7476
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="max-w-4xl mx-auto px-6 py-12 prose prose-slate prose-lg max-w-none">
        <div dangerouslySetInnerHTML={{ __html: c.body || '' }} />

        {c.local_section && (
          <div className="bg-slate-50 border-l-4 border-blue-600 p-6 my-8 rounded-r">
            <h2 className="text-2xl font-bold mb-3">Why {page.city_name} Businesses Choose Us</h2>
            <div dangerouslySetInnerHTML={{ __html: c.local_section }} />
          </div>
        )}

        {c.cta && (
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 rounded-lg my-8 text-center">
            <div dangerouslySetInnerHTML={{ __html: c.cta }} />
            <div className="flex gap-3 justify-center mt-5 flex-wrap">
              <Button size="lg" onClick={() => navigate('/book-consultation')}
                      className="bg-white text-blue-700 hover:bg-blue-50" data-testid="geo-cta-btn">
                Get Started in {page.city_name}
              </Button>
            </div>
          </div>
        )}
      </article>

      {/* FAQ */}
      {Array.isArray(c.faq) && c.faq.length > 0 && (
        <section className="bg-slate-50 py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-slate-900">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {c.faq.map((item, i) => (
                <details key={i} className="bg-white p-5 rounded-lg border border-slate-200 group">
                  <summary className="font-semibold cursor-pointer text-slate-900 flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    {item.q}
                  </summary>
                  <p className="mt-3 text-slate-700 leading-relaxed pl-7">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sister services in this city */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-slate-900">
            Other Services for {page.city_name} Businesses
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(SERVICE_NAMES)
              .filter(([sid]) => sid !== serviceId)
              .map(([sid, label]) => (
                <Link
                  key={sid}
                  to={`/locations/${stateSlug}/${citySlug}/${sid}`}
                  className="border border-slate-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition group"
                  data-testid={`related-service-${sid}`}
                >
                  <div className="font-semibold text-slate-900 group-hover:text-blue-600">{label}</div>
                  <div className="text-xs text-slate-500 mt-1">in {page.city_name}</div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
